import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, ArrowUpDown, X } from "lucide-react";
import { AdvancedFilterModal, type AdvancedFilters, defaultAdvancedFilters } from "./AdvancedFilterModal";
import { applyAdvancedFilters, computeDuplicateGroups, type EnrichedProfile } from "./userFilters";
import { toast } from "sonner";

type SortKey =
  | "created_desc"
  | "created_asc"
  | "name_asc"
  | "name_desc"
  | "warnings_desc"
  | "warnings_asc"
  | "listings_desc"
  | "listings_asc"
  | "rating_desc"
  | "rating_asc";

interface UsersTabProps {
  onSelectProfile: (p: any) => void;
  onApproveCompany: (id: string) => Promise<void> | void;
  registerRemove?: (fn: (id: string) => void) => void;
}

export function UsersTab({ onSelectProfile, onApproveCompany, registerRemove }: UsersTabProps) {
  const [profiles, setProfiles] = useState<EnrichedProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("created_desc");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<AdvancedFilters>(defaultAdvancedFilters);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [profilesRes, listingsRes, reviewsRes, purchasesRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("*").order("created_at", { ascending: false }),
        supabase.from("listings").select("seller_id, status"),
        supabase.from("purchase_reviews").select("target_user_id, rating"),
        supabase.from("purchase_requests").select("buyer_id, seller_id, status"),
        supabase.from("user_roles").select("user_id, role"),
      ]);

      if (profilesRes.error) {
        toast.error("Kunde inte hämta användare");
        setLoading(false);
        return;
      }

      const listingsByUser: Record<string, number> = {};
      (listingsRes.data || []).forEach((l: any) => {
        if (l.status === "aktiv") {
          listingsByUser[l.seller_id] = (listingsByUser[l.seller_id] || 0) + 1;
        }
      });

      const reviewsByUser: Record<string, { count: number; sum: number }> = {};
      (reviewsRes.data || []).forEach((r: any) => {
        const k = r.target_user_id;
        if (!k) return;
        reviewsByUser[k] = reviewsByUser[k] || { count: 0, sum: 0 };
        reviewsByUser[k].count += 1;
        reviewsByUser[k].sum += r.rating || 0;
      });

      const completedByUser: Record<string, number> = {};
      (purchasesRes.data || []).forEach((p: any) => {
        if (p.status === "completed" || p.status === "paid_out" || p.status === "delivered") {
          if (p.buyer_id) completedByUser[p.buyer_id] = (completedByUser[p.buyer_id] || 0) + 1;
          if (p.seller_id) completedByUser[p.seller_id] = (completedByUser[p.seller_id] || 0) + 1;
        }
      });

      const rolesByUser: Record<string, string[]> = {};
      (rolesRes.data || []).forEach((r: any) => {
        rolesByUser[r.user_id] = rolesByUser[r.user_id] || [];
        rolesByUser[r.user_id].push(r.role);
      });

      const enriched: EnrichedProfile[] = (profilesRes.data || []).map((p: any) => {
        const rev = reviewsByUser[p.id];
        return {
          ...p,
          _activeListings: listingsByUser[p.id] || 0,
          _reviewsCount: rev?.count || 0,
          _avgRating: rev && rev.count > 0 ? rev.sum / rev.count : 0,
          _completedPurchases: completedByUser[p.id] || 0,
          _warnings: 0, // Warnings system not implemented yet — defaults to 0
          _roles: rolesByUser[p.id] || [],
        };
      });

      setProfiles(enriched);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (registerRemove) {
      registerRemove((id: string) => setProfiles((prev) => prev.filter((p) => p.id !== id)));
    }
  }, [registerRemove]);

  // Free-text search across name, email, phone, kommun, province, org.no
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => {
      const fields = [
        p.id,
        p.full_name,
        p.email,
        p.business_email,
        p.phone,
        p.whatsapp_number,
        p.municipality,
        p.province,
        p.organization_number,
        p.company_name,
      ];
      return fields.some((f) => f && String(f).toLowerCase().includes(q));
    });
  }, [profiles, search]);

  // Apply advanced filters
  const filtered = useMemo(() => applyAdvancedFilters(searched, filters), [searched, filters]);

  // Duplicate groups (if any duplicate criteria active)
  const duplicateGroups = useMemo(() => computeDuplicateGroups(filtered, filters.duplicates), [filtered, filters.duplicates]);
  const duplicateMode = duplicateGroups !== null;

  // Sort
  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "created_desc":
        arr.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
        break;
      case "created_asc":
        arr.sort((a, b) => +new Date(a.created_at) - +new Date(b.created_at));
        break;
      case "name_asc":
        arr.sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "", "sv"));
        break;
      case "name_desc":
        arr.sort((a, b) => (b.full_name || "").localeCompare(a.full_name || "", "sv"));
        break;
      case "warnings_desc":
        arr.sort((a, b) => b._warnings - a._warnings);
        break;
      case "warnings_asc":
        arr.sort((a, b) => a._warnings - b._warnings);
        break;
      case "listings_desc":
        arr.sort((a, b) => b._activeListings - a._activeListings);
        break;
      case "listings_asc":
        arr.sort((a, b) => a._activeListings - b._activeListings);
        break;
      case "rating_desc":
        arr.sort((a, b) => b._avgRating - a._avgRating);
        break;
      case "rating_asc":
        arr.sort((a, b) => a._avgRating - b._avgRating);
        break;
    }
    return arr;
  }, [filtered, sort]);

  const activeFilterCount = countActiveFilters(filters);

  const handleSelect = async (p: EnrichedProfile) => {
    onSelectProfile(p);
  };

  return (
    <div>
      {/* Search + actions */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök på namn, e-post, telefon, kommun, län, org.nr eller användar-ID..."
            className="pl-9"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent"
              aria-label="Rensa sök"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setFilterOpen(true)} className="relative">
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Avancerat filter
            {activeFilterCount > 0 && (
              <Badge className="ml-2 h-5 min-w-5 rounded-full px-1.5 text-[10px]">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
            <SelectTrigger className="w-[200px]">
              <ArrowUpDown className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Sortering" />
            </SelectTrigger>
            <SelectContent className="z-[1100]">
              <SelectItem value="created_desc">Nyast först</SelectItem>
              <SelectItem value="created_asc">Äldst först</SelectItem>
              <SelectItem value="name_asc">Namn A–Ö</SelectItem>
              <SelectItem value="name_desc">Namn Ö–A</SelectItem>
              <SelectItem value="warnings_desc">Flest varningar</SelectItem>
              <SelectItem value="warnings_asc">Färst varningar</SelectItem>
              <SelectItem value="listings_desc">Flest annonser</SelectItem>
              <SelectItem value="listings_asc">Färst annonser</SelectItem>
              <SelectItem value="rating_desc">Högst betyg</SelectItem>
              <SelectItem value="rating_asc">Lägst betyg</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Counter */}
      <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
        <p>
          Visar <span className="font-semibold text-foreground">{sorted.length}</span> av{" "}
          <span className="font-semibold text-foreground">{profiles.length}</span> användare
          {duplicateMode && (
            <>
              {" "}· <span className="font-semibold text-foreground">{duplicateGroups!.length}</span>{" "}
              dubblettgrupp(er)
            </>
          )}
        </p>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilters(defaultAdvancedFilters)}
          >
            Rensa filter
          </Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center text-muted-foreground py-8">Laddar...</p>
      ) : sorted.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">Inga användare matchar.</p>
      ) : duplicateMode ? (
        <div className="space-y-4">
          {duplicateGroups!.map((g, i) => (
            <div key={i} className="rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/20 p-3">
              <p className="mb-2 text-xs font-semibold text-amber-800 dark:text-amber-200">
                {g.label}: <span className="font-mono">{g.value}</span> · {g.users.length} konton
              </p>
              <div className="space-y-1.5">
                {g.users.map((p) => (
                  <UserRow key={p.id} p={p} onClick={() => handleSelect(p)} onApprove={onApproveCompany} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((p) => (
            <UserRow key={p.id} p={p} onClick={() => handleSelect(p)} onApprove={onApproveCompany} />
          ))}
        </div>
      )}

      <AdvancedFilterModal
        open={filterOpen}
        onOpenChange={setFilterOpen}
        value={filters}
        onChange={setFilters}
      />
    </div>
  );
}

function UserRow({
  p,
  onClick,
  onApprove,
}: {
  p: EnrichedProfile;
  onClick: () => void;
  onApprove: (id: string) => Promise<void> | void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      className="flex items-center justify-between rounded-lg border border-border bg-card p-3 cursor-pointer hover:bg-accent/50 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm flex items-center gap-1.5 flex-wrap">
          <span className="truncate">{p.full_name || "Namnlös"}</span>
          {p.account_type && (
            <Badge variant="secondary" className="text-[10px]">{p.account_type}</Badge>
          )}
          {p.account_type === "company" && p.admin_approved && (
            <Badge className="text-[10px]">Verifierat</Badge>
          )}
          {p._roles.includes("admin") && (
            <Badge variant="outline" className="text-[10px] border-primary text-primary">admin</Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {p.email || "—"} · {[p.province, p.municipality].filter(Boolean).join("/") || "Ingen ort"} · {p.phone || "Inget tel"}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          {p._activeListings} aktiva annonser · {p._reviewsCount} omdömen
          {p._reviewsCount > 0 && ` (${p._avgRating.toFixed(1)}★)`} · {p._completedPurchases} avslutade köp
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
        {p.account_type === "company" && !p.admin_approved && (
          <Button size="sm" onClick={() => onApprove(p.id)}>Godkänn</Button>
        )}
        <p className="text-xs text-muted-foreground">
          {new Date(p.created_at).toLocaleDateString("sv-SE")}
        </p>
      </div>
    </div>
  );
}

function countActiveFilters(f: AdvancedFilters): number {
  let n = 0;
  if (f.accountType.include.length || f.accountType.exclude.length) n++;
  if (f.accountStatus.include.length || f.accountStatus.exclude.length) n++;
  if (f.verificationStatus.include.length || f.verificationStatus.exclude.length) n++;
  if (f.province.include.length || f.province.exclude.length) n++;
  if (f.municipality.include.length || f.municipality.exclude.length) n++;
  if (f.registeredFrom || f.registeredTo) n++;
  if (f.activeListings.min !== "" || f.activeListings.max !== "") n++;
  if (f.warnings.min !== "" || f.warnings.max !== "") n++;
  if (f.completedPurchases.min !== "" || f.completedPurchases.max !== "") n++;
  if (f.reviewsCount.min !== "" || f.reviewsCount.max !== "") n++;
  if (f.avgRating.min !== "" || f.avgRating.max !== "") n++;
  if (f.hasSocial !== "any") n++;
  if (f.hasCover !== "any") n++;
  if (f.hasAvatar !== "any") n++;
  if (f.duplicates.email || f.duplicates.phone || f.duplicates.organization_number || f.duplicates.name !== "off" || f.duplicates.kommun_name) n++;
  return n;
}
